import { useState, useEffect } from "react";
import { Users, Percent, DollarSign, Star, BarChart3, EyeOff, Eye, CalendarDays, SlidersHorizontal, Check } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { SingleDatePicker } from "@/components/SingleDatePicker";
import { parseTourDates, gratuityColor, isFutureRequest } from "@/lib/fields";
import { usePrivacy } from "@/contexts/PrivacyContext";

const money = (n) => "$" + Math.round(n || 0).toLocaleString();
const FINANCIAL_METRIC_KEYS = ["tip", "gratuity"];

const METRICS = [
  { key: "tours", label: "Tours", icon: Users, compute: (r) => r.length },
  { key: "tip", label: "Avg Tip %", icon: Percent, compute: (r, h) => `${h.avg(h.nums(r, "tip_percent")).toFixed(1)}%`, color: (r, h) => gratuityColor(h.avg(h.nums(r, "tip_percent"))) },
  { key: "gratuity", label: "Total Tips", icon: DollarSign, compute: (r, h) => money(h.sum(r, "gratuity")) },
  { key: "rating", label: "Avg Rating", icon: Star, compute: (r, h) => h.avg(h.nums(r, "rating")).toFixed(2) },
  { key: "days", label: "Total Days", icon: CalendarDays, compute: (r) => r.reduce((a, t) => a + (typeof t.days === "number" && t.days ? t.days : 1), 0) },
  { key: "retail", label: "Total Retail", icon: DollarSign, compute: (r, h) => money(h.sum(r, "retail_value")) },
];
const DEFAULT_KEYS = ["tours", "tip", "gratuity", "rating"];

const boundsFor = (range, from, to) => {
  const t = new Date(); t.setHours(0, 0, 0, 0);
  if (range === "last30") { const f = new Date(t); f.setDate(t.getDate() - 29); return [f, null]; }
  if (range === "month") return [new Date(t.getFullYear(), t.getMonth(), 1), null];
  if (range === "year") return [new Date(t.getFullYear(), 0, 1), null];
  if (range === "custom") {
    const f = from ? new Date(from + "T00:00:00") : null;
    const e = to ? new Date(to + "T00:00:00") : null;
    if (e) e.setHours(23, 59, 59, 999);
    return [f, e];
  }
  return null;
};

const Stat = ({ icon: Icon, label, value, testid, valueClass }) => (
  <div className="rounded-2xl border border-zinc-800/80 bg-zinc-900/40 p-4 transition-transform duration-150 hover:-translate-y-0.5" data-testid={testid}>
    <div className="mb-2.5 flex h-8 w-8 items-center justify-center rounded-lg bg-[#9B2226]/12 text-[#E0575B]">
      <Icon className="h-4 w-4" />
    </div>
    <div className={`font-mono text-2xl font-700 tracking-tight ${valueClass || "text-zinc-50"}`}>{value}</div>
    <div className="mt-0.5 text-[11px] uppercase tracking-wide text-zinc-400">{label}</div>
  </div>
);

export const StatsStrip = ({ tours = [], governed = false }) => {
  const { financialPrivacy } = usePrivacy();
  const [hidden, setHidden] = useState(() => localStorage.getItem("statsStripHidden") === "1");
  const [range, setRange] = useState(() => localStorage.getItem("statsStripRange") || "all");
  const [from, setFrom] = useState(() => localStorage.getItem("statsStripFrom") || "");
  const [to, setTo] = useState(() => localStorage.getItem("statsStripTo") || "");
  // Remember the last-used stats filter so the insights are the same on return.
  useEffect(() => {
    localStorage.setItem("statsStripRange", range);
    localStorage.setItem("statsStripFrom", from);
    localStorage.setItem("statsStripTo", to);
  }, [range, from, to]);
  const [keys, setKeys] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem("statsStripKeys")); return Array.isArray(s) && s.length ? s : DEFAULT_KEYS; }
    catch { return DEFAULT_KEYS; }
  });

  const setHide = (v) => { setHidden(v); localStorage.setItem("statsStripHidden", v ? "1" : "0"); };
  const toggleKey = (k) => setKeys((prev) => {
    const next = prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k];
    localStorage.setItem("statsStripKeys", JSON.stringify(next));
    return next;
  });

  if (hidden) {
    return (
      <button onClick={() => setHide(false)} className="mb-3 inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-300 hover:text-zinc-100" data-testid="stats-show-btn">
        <Eye className="h-4 w-4" /> Show stats
      </button>
    );
  }

  const b = governed ? null : boundsFor(range, from, to);
  const inRange = (t) => {
    if (!b) return true;
    const [f, e] = b;
    const ds = parseTourDates(t.tour_date);
    return ds.length && ds.some((d) => (!f || d >= f) && (!e || d <= e));
  };
  const isPossible = (t) => isFutureRequest(t);
  const rows = tours.filter((t) => !isPossible(t)).filter(inRange);
  const h = {
    nums: (r, k) => r.map((t) => t[k]).filter((v) => typeof v === "number"),
    avg: (arr) => (arr.length ? arr.reduce((a, c) => a + c, 0) / arr.length : 0),
    sum: (r, k) => r.map((t) => t[k]).filter((v) => typeof v === "number").reduce((a, c) => a + c, 0),
  };
  const menuMetrics = METRICS.filter((m) => !(financialPrivacy && FINANCIAL_METRIC_KEYS.includes(m.key)));
  const shownRaw = METRICS.filter((m) => keys.includes(m.key) && !(financialPrivacy && FINANCIAL_METRIC_KEYS.includes(m.key)));
  const shown = shownRaw.length ? shownRaw : menuMetrics.filter((m) => ["tours", "rating"].includes(m.key));

  return (
    <div className="mb-4" data-testid="stats-strip">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-500 text-zinc-300">
          <BarChart3 className="h-4 w-4 text-[#E0575B]" /> Insights
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <button className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-2 text-sm text-zinc-300 hover:text-zinc-100" data-testid="stats-fields-btn">
                <SlidersHorizontal className="h-4 w-4" /> Fields
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-52 lg-glass-strong border-0 p-1.5" data-testid="stats-fields-menu">
              <p className="px-2 py-1.5 text-xs font-600 uppercase tracking-wide text-zinc-400">Show fields</p>
              {menuMetrics.map((m) => {
                const on = keys.includes(m.key);
                return (
                  <button
                    key={m.key}
                    onClick={() => toggleKey(m.key)}
                    disabled={on && keys.length === 1}
                    className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm text-zinc-200 hover:bg-white/5 disabled:opacity-40"
                    data-testid={`stats-field-${m.key}`}
                  >
                    <span className="flex items-center gap-2"><m.icon className="h-4 w-4 text-zinc-400" /> {m.label}</span>
                    {on && <Check className="h-4 w-4 text-[#E0575B]" />}
                  </button>
                );
              })}
            </PopoverContent>
          </Popover>
          {!governed && (
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-[150px] border-zinc-800 bg-zinc-900 focus:ring-[#E0575B]/70" data-testid="stats-range-select"><SelectValue /></SelectTrigger>
            <SelectContent className="border-zinc-800 bg-zinc-950">
              <SelectItem value="all" data-testid="stats-range-all">All time</SelectItem>
              <SelectItem value="last30" data-testid="stats-range-last30">Last 30 days</SelectItem>
              <SelectItem value="month" data-testid="stats-range-month">This month</SelectItem>
              <SelectItem value="year" data-testid="stats-range-year">This year</SelectItem>
              <SelectItem value="custom" data-testid="stats-range-custom">Custom range…</SelectItem>
            </SelectContent>
          </Select>
          )}
          {!governed && range === "custom" && (
            <div className="flex items-center gap-1.5">
              <SingleDatePicker value={from} onChange={setFrom} testid="stats-from" placeholder="From" className="w-[150px]" />
              <span className="text-xs text-zinc-400">to</span>
              <SingleDatePicker value={to} onChange={setTo} testid="stats-to" placeholder="To" className="w-[150px]" />
            </div>
          )}
          <button onClick={() => setHide(true)} className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-2 text-sm text-zinc-300 hover:text-zinc-100" data-testid="stats-hide-btn">
            <EyeOff className="h-4 w-4" /> Hide
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {shown.map((m) => (
          <Stat key={m.key} icon={m.icon} label={m.label} value={m.compute(rows, h)} valueClass={m.color ? m.color(rows, h) : undefined} testid={`stat-${m.key}`} />
        ))}
      </div>
    </div>
  );
};
